import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import toast from "react-hot-toast";
import { Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";

import BodyHeader from "../../../components/common/CommonBodyHeader";
import CustomInput from "../../../components/common/CustomInput/CustomInput";
import { backdropLoadingAction } from "../../../redux/features/helperSlice";
import { getDriverDropdownDataAsync } from "../../../redux/features/fleetMaster";
import { listTripByPaginationApiCall } from "../../../Api/api";
import MISReport from "./MISReport";
import { ROLES } from "../../../constansts/Roles";
import TripTable from "./TripTable";
import { useDebounce } from "../../../Hooks/useDebounce";

export default function ContractualTripList() {
   const dispatch = useDispatch();
   const navigate = useNavigate();
   const [params, setSearchParams] = useSearchParams();

   const trip_status = params.get("trip_status") || "";
   const review_status = params.get("review_status") || "";
   const searchParam = params.get("search") || "";
   const initialPage = Number(params.get("page")) || 0;

   const [records, setRecords] = useState([]);
   const [listPagination, setListPagination] = useState(null);
   const [limit, setLimit] = useState(10);
   const [page, setPage] = useState(initialPage);
   const [tripStatusFilter, setTripStatusFilter] = useState(trip_status);
   const [searchTerm, setSearchTerm] = useState(searchParam);

   const debouncedSearchTerm = useDebounce(searchTerm, 500);

   const userRoleId = useSelector((state) => state?.auth?.user?.data?.user?.role_id);
   const userId = useSelector((state) => state?.auth?.user?.data?.user?.id);

   let roleName;
   for (const key in ROLES) {
      if (ROLES[key] === userRoleId) {
         roleName = key;
      }
   }

   const fetchTrips = useCallback(
      async (search = "") => {
         if (!roleName || !userId) return;

         dispatch(backdropLoadingAction(true));

         try {
            const { data: response } = await listTripByPaginationApiCall(
               roleName,
               userId,
               limit,
               page,
               trip_status,
               review_status,
               search,
            );
            if (response.statusCode === 200) {
               setRecords(response?.data);
               setListPagination(response?.pagination);
            }
         } catch (error) {
            //toast.error("Failed to fetch trips.");
         } finally {
            dispatch(backdropLoadingAction(false));
         }
      },
      [limit, page, roleName, userId, review_status, trip_status, dispatch],
   );

   const updateQueryParams = (overrides = {}) => {
      setSearchParams({
         trip_status: overrides.trip_status ?? trip_status ?? "",
         review_status: overrides.review_status ?? review_status ?? "",
         page: overrides.page ?? page,
         search: overrides.search ?? searchTerm ?? "",
      });
   };

   const handlePageChange = (newPage) => {
      setPage(newPage);
      updateQueryParams({ page: newPage });
   };

   useEffect(() => {
      fetchTrips(debouncedSearchTerm);
   }, [fetchTrips, debouncedSearchTerm]);

   useEffect(() => {
      dispatch(getDriverDropdownDataAsync());
   }, [dispatch]);

   const handleStatusFilter = (filterName, status) => {
      const newStatus = status === "all" ? "" : status;
      setTripStatusFilter(newStatus);
      setPage(0); // reset page only when filter changes
      updateQueryParams({
         trip_status: filterName === "trip_status" ? newStatus : trip_status,
         review_status: filterName === "review_status" ? newStatus : review_status,
         page: 0,
      });
   };

   const handleFilter = (e) => {
      const searchText = e.target.value;
      setSearchTerm(searchText);

      // reset to first page for new search
      setPage(0);
      updateQueryParams({ page: 0, search: searchText });
   };

   useEffect(() => {
      setSearchTerm(searchParam);
   }, [searchParam]);

   return (
      <div>
         <BodyHeader title="Trip List" />

         <div className="px-3">
            <div className="d-flex justify-content-between align-items-center mb-2 status-filters">
               <Stack direction="row" alignItems="center" spacing={1}>
                  <div>
                     <Typography>Trip Status</Typography>
                     <ToggleButtonGroup
                        color="primary"
                        value={trip_status}
                        exclusive
                        onChange={(_, value) => handleStatusFilter("trip_status", value)}
                        aria-label="Trip Status"
                     >
                        <ToggleButton value="">All</ToggleButton>
                        <ToggleButton value="open">Open Trip</ToggleButton>
                        <ToggleButton value="closed">Closed Trip</ToggleButton>
                     </ToggleButtonGroup>
                  </div>
                  <div>
                     <Typography>Review Status</Typography>
                     <ToggleButtonGroup
                        color="primary"
                        value={review_status}
                        exclusive
                        onChange={(_, value) =>
                           handleStatusFilter("review_status", value)
                        }
                        aria-label="Review Status"
                     >
                        <ToggleButton value="">All</ToggleButton>
                        <ToggleButton value="pending">Pending</ToggleButton>
                        <ToggleButton value="approved">Approved</ToggleButton>
                        <ToggleButton value="rejected">Rejected</ToggleButton>
                     </ToggleButtonGroup>
                  </div>
               </Stack>
               <div className="d-flex gap-2 mt-4">
                  <CustomInput
                     inputType="text"
                     placeholder="Search..."
                     id="search"
                     value={searchTerm}
                     onChange={(e) => handleFilter(e)}
                     containerStyle={{ marginRight: 12 }}
                  />
                  <MISReport />
               </div>
            </div>

            <TripTable
               rows={records}
               limit={limit}
               page={page}
               setLimit={setLimit}
               setPage={handlePageChange}
               listPagination={listPagination}
            />
         </div>
      </div>
   );
}
